import Select, { Option } from '@veeva/corgix/select';
import { ThemeProvider, useTheme } from 'emotion-theming';
import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import AppContext from '../../../common/containers/AppContext';
import filterCustomers from '../../services/filterCustomers';
import getShortenedPodName from '../../services/getShortenedPodName';
import CustomerStatFilterContext from './CustomerStatFilterContext';
import {
    customerStatFilterInstanceDescriptionInputCSS,
    customerStatFilterMobileMultiSelectAllCustomersCSS,
    customerStatFilterMobileMultiSelectCSS,
    customerStatFilterMultiSelectAllCustomersCSS,
    customerStatFilterMultiSelectCSS,
    customerStatFilterSingleSelectCSS,
    customerStatFilterSingleSelectTopDownCSS,
    customerStatFilterTopDownMultiSelectAllCustomersCSS,
    customerStatFilterTopDownMultiSelectCSS,
} from './styles/FilterSelectionMenu.styles';
import FilterSelectionStatInput from './FilterSelectionStatInput';
import pageGroupType from '../PageGroupType';
import useGetApplicationsWithStatsByFamilyId from '../../services/data/useGetApplicationsWithStatsByFamilyId';

export default function FilterSelectionMenu({
    filterRowId,
    filterType,
    filterStat,
    handleSetFilters,
    applications,
    customers,
    filter,
    topDown,
    mobile,
    operator,
    filterStatType,
    setFilterStatType,
}) {
    const { selectedCustomerFilterList, pageGroup } = useContext(CustomerStatFilterContext);
    const { regions, customerSizes, industries, segments, families } = useContext(AppContext);
    const [searchText, setSearchText] = useState('');
    const maxCustomersToDisplay = 20;
    const customersToDisplay = filterCustomers(customers, searchText).splice(
        0,
        maxCustomersToDisplay,
    );

    const appFamilies = {};
    const appsList = {};

    if (pageGroup === pageGroupType.ALL_CUSTOMERS) {
        families.forEach((family) => {
            appFamilies[family.key] = family.label;
            // disabling because function must be called per family id, cannot be called once
            // eslint-disable-next-line  react-hooks/rules-of-hooks
            const [applicationsWithAvailableStats] = useGetApplicationsWithStatsByFamilyId(
                Number(family?.id),
            );
            applicationsWithAvailableStats.forEach((app) => {
                if (family.label !== app.label) {
                    appsList[app.key] = `${family.label} > ${app.label}`;
                }
            });
        });
    }

    const handleSearchInputChange = (e) => {
        setSearchText(e);
    };

    const appKeys = new Set(filter?.appKeys ? filter.appKeys : []);
    const vaultApplications =
        filterType === 'Application' && filter?.appKeys
            ? applications
                  .filter((app) => appKeys.has(app.key))
                  .map(({ key, label }) => ({
                      label,
                      value: key,
                  }))
            : [];
    const customerSet = new Set(
        filter?.customerIds ? filter.customerIds.map((id) => parseInt(id, 10)) : [],
    );
    const vaultCustomers =
        filterType === 'Customers' && filter?.customerIds
            ? customers
                  .filter((customer) => customerSet.has(Number(customer.id)))
                  .map(({ id, label }) => ({
                      label,
                      value: id,
                  }))
            : [];

    const customerSizesSet = new Set(filter?.customerSizes ? filter.customerSizes : []);
    const customerSize =
        filterType === 'Customer Size' && filter?.customerSizes
            ? [
                  ...customerSizes
                      .filter((s) => customerSizesSet.has(s))
                      .map((s) => ({
                          label: s,
                          value: s,
                      })),
              ]
            : [];

    const regionSet = new Set(filter?.regions ? filter.regions : []);
    const region =
        filterType === 'Customer Region' && filter?.regions
            ? [
                  ...regions
                      .filter((r) => regionSet.has(r))
                      .map((r) => ({
                          label: r,
                          value: r,
                      })),
              ]
            : [];

    const industrySet = new Set(filter?.industries ? filter.industries : []);
    const industry =
        filterType === 'Customer Industry' && filter?.industries
            ? [
                  ...industries
                      .filter((i) => industrySet.has(i))
                      .map((i) => ({
                          label: i,
                          value: i,
                      })),
              ]
            : [];

    const segmentSet = new Set(filter?.segments ? filter.segments : []);
    const segment =
        filterType === 'Customer Segment' && filter?.segments
            ? [
                  ...segments
                      .filter((s) => segmentSet.has(s))
                      .map((s) => ({
                          label: s,
                          value: s,
                      })),
              ]
            : [];

    const vaultBasicsSelection =
        filterType === 'Basics' && filter?.vaultBasics
            ? { value: filter.vaultBasics, label: filter.vaultBasics }
            : {};
    const availableSizes = {
        small: 'Small',
        medium: 'Medium',
        large: 'Large',
        veryLarge: 'Very Large',
        extraLarge: 'Extra Large',
        full: 'Full',
    };
    const vaultSize =
        filterType === 'Vault Size' && filter?.vaultSizes
            ? filter.vaultSizes.map((size) => ({
                  value: size,
                  label: availableSizes[size],
              }))
            : [];

    const vaultPod =
        filterType === 'POD' && filter?.podNames
            ? filter.podNames.map((podName) => ({
                  value: podName,
                  label: getShortenedPodName(podName),
              }))
            : [];

    const applicationsSet = new Set(filter?.applications ? filter.applications : []);
    const application =
        filterType === 'Applications' && filter?.applications
            ? [
                  ...Object.entries(appsList)
                      .filter(([key]) => applicationsSet.has(key))
                      .map(([key, label]) => ({
                          key: String(key),
                          value: String(key),
                          label,
                      })),
              ]
            : [];

    const appFamilySet = new Set(filter?.appFamilyKeys ? filter.appFamilyKeys : []);
    const appFamily =
        filterType === 'Application Families' && filter?.appFamilyKeys
            ? [
                  ...Object.entries(appFamilies)
                      .filter(([key]) => appFamilySet.has(key))
                      .map(([key, label]) => ({
                          key: String(key),
                          value: String(key),
                          label,
                      })),
              ]
            : [];

    const handleSetApps = (e, newValue) => {
        const updatedApps = newValue.map((key) => key.value);
        const appFilter = updatedApps?.length > 0 ? { appKeys: updatedApps } : null;
        handleSetFilters(filterRowId, appFilter, vaultApplications.length > 0, 'Application');
    };

    const handleSetCustomers = (e, newValue) => {
        const updatedIds = newValue.map((key) => Number(key.value));
        const customerFilter = updatedIds?.length > 0 ? { customerIds: updatedIds } : null;
        handleSetFilters(filterRowId, customerFilter, vaultCustomers.length > 0, 'Customers');
    };

    const handleSetBasics = (e, newValue) => {
        const basicsFilter = newValue !== undefined ? { vaultBasics: newValue } : null;
        handleSetFilters(
            filterRowId,
            basicsFilter,
            vaultBasicsSelection.value !== undefined,
            'Basics',
        );
    };

    const handleSetVaultSizes = (e, newValue) => {
        const updatedSizes = newValue.map((key) => key.value);
        const vaultSizeFilter = updatedSizes?.length > 0 ? { vaultSizes: updatedSizes } : null;
        handleSetFilters(filterRowId, vaultSizeFilter, vaultSize.length > 0, 'Vault Size');
    };

    const handleSetCustomerSizes = (e, newValue) => {
        const updatedSizes = newValue.map((key) => key.value);
        const customerSizeFilter =
            updatedSizes?.length > 0 ? { customerSizes: updatedSizes } : null;
        handleSetFilters(filterRowId, customerSizeFilter, customerSize.length > 0, 'Customer Size');
    };

    const handleSetRegions = (e, newValue) => {
        const updatedRegions = newValue.map((key) => key.value);
        const regionFilter = updatedRegions?.length > 0 ? { regions: updatedRegions } : null;
        handleSetFilters(filterRowId, regionFilter, region.length > 0, 'Customer Region');
    };

    const handleSetSegments = (e, newValue) => {
        const updatedSegments = newValue.map((key) => key.value);
        const segmentFilter = updatedSegments?.length > 0 ? { segments: updatedSegments } : null;
        handleSetFilters(filterRowId, segmentFilter, segment.length > 0, 'Customer Segment');
    };

    const handleSetInstanceDescription = (e) => {
        const value = e.target.value;
        const instanceDescriptionFilter = value ? { instanceDescription: value } : null;
        handleSetFilters(
            filterRowId,
            instanceDescriptionFilter,
            !!filter?.instanceDescription,
            'Additional Info',
        );
    };

    const handleSetIndustries = (e, newValue) => {
        const updatedIndustries = newValue.map((key) => key.value);
        const industryFilter =
            updatedIndustries?.length > 0 ? { industries: updatedIndustries } : null;
        handleSetFilters(filterRowId, industryFilter, industry.length > 0, 'Customer Industry');
    };

    const handleSetPod = (e, newValue) => {
        const updatedPodNames = newValue.map((key) => key.value);
        const podFilter = updatedPodNames?.length > 0 ? { podNames: updatedPodNames } : null;
        handleSetFilters(filterRowId, podFilter, vaultPod.length > 0, 'POD');
    };

    const handleSetApplications = (e, newValue) => {
        const updatedApps = newValue.map((key) => key.value);
        const applicationFilter =
            updatedApps?.length > 0 ? { applications: updatedApps, filterType: operator } : null;
        handleSetFilters(filterRowId, applicationFilter, application.length > 0, 'Applications');
    };

    const handleSetAppFamilies = (e, newValue) => {
        const updatedAppFamilies = newValue.map((key) => key.value);
        const appFamilyFilter =
            updatedAppFamilies?.length > 0
                ? { appFamilyKeys: updatedAppFamilies, filterType: operator }
                : null;
        handleSetFilters(
            filterRowId,
            appFamilyFilter,
            appFamily.length > 0,
            'Application Families',
        );
    };

    const { podNames } = useContext(AppContext);

    const { colors } = useTheme();
    const themeOverrides = {
        tagBackgroundColorDefault: colors.blueLight,
        tagBorderRadius: '0.25rem',
    };
    const getMultiSelectCSS = () => {
        if (pageGroup === pageGroupType.ALL_CUSTOMERS) {
            if (mobile) {
                return customerStatFilterMobileMultiSelectAllCustomersCSS;
            }
            if (topDown) {
                return customerStatFilterTopDownMultiSelectAllCustomersCSS;
            }
            return customerStatFilterMultiSelectAllCustomersCSS;
        }
        if (mobile) {
            return customerStatFilterMobileMultiSelectCSS;
        }
        if (topDown) {
            return customerStatFilterTopDownMultiSelectCSS;
        }
        return customerStatFilterMultiSelectCSS;
    };

    if (pageGroup === pageGroupType.ALL_CUSTOMERS) {
        switch (filterType) {
            case 'Applications':
                return (
                    <ThemeProvider theme={themeOverrides}>
                        <Select
                            multiple
                            onChange={handleSetApplications}
                            value={application}
                            required
                            css={getMultiSelectCSS()}
                            key={`${filterRowId}-Applications`}
                        >
                            {Object.entries(appsList)?.map(([key, label]) => (
                                <Option key={key} value={key}>
                                    {label}
                                </Option>
                            ))}
                        </Select>
                    </ThemeProvider>
                );
            case 'Application Families':
                return (
                    <ThemeProvider theme={themeOverrides}>
                        <Select
                            multiple
                            onChange={handleSetAppFamilies}
                            value={appFamily}
                            required
                            css={getMultiSelectCSS()}
                            key={`${filterRowId}-Application-Families`}
                            onSearch={handleSearchInputChange}
                            disabled={selectedCustomerFilterList === 'redReport'}
                        >
                            {Object.entries(appFamilies)?.map(([key, label]) => (
                                <Option key={key} value={key}>
                                    {label}
                                </Option>
                            ))}
                        </Select>
                    </ThemeProvider>
                );
            default:
                return null;
        }
    } else {
        if (filterType.includes('Stats')) {
            return (
                <FilterSelectionStatInput
                    stat={filterStat}
                    handleSetFilters={handleSetFilters}
                    filterRowId={filterRowId}
                    filter={filter}
                    operator={operator}
                    filterStatType={filterStatType}
                    setFilterStatType={setFilterStatType}
                    disabled={selectedCustomerFilterList === 'redReport'}
                />
            );
        }
        switch (filterType) {
            case 'Application':
                return (
                    <ThemeProvider theme={themeOverrides}>
                        <Select
                            multiple
                            onChange={handleSetApps}
                            value={vaultApplications}
                            required
                            css={getMultiSelectCSS()}
                            key={`${filterRowId}-Application`}
                        >
                            {applications?.map(({ key, label }) => (
                                <Option key={label} value={key}>
                                    {label}
                                </Option>
                            ))}
                        </Select>
                    </ThemeProvider>
                );
            case 'Customers':
                return (
                    <ThemeProvider theme={themeOverrides}>
                        <Select
                            multiple
                            onChange={handleSetCustomers}
                            value={vaultCustomers}
                            required
                            css={getMultiSelectCSS()}
                            key={`${filterRowId}-Customers`}
                            onSearch={handleSearchInputChange}
                            disabled={selectedCustomerFilterList === 'redReport'}
                        >
                            {customersToDisplay?.map(({ id, label }) => (
                                <Option key={label} value={id}>
                                    {label}
                                </Option>
                            ))}
                        </Select>
                    </ThemeProvider>
                );
            case 'Basics':
                return (
                    <ThemeProvider theme={themeOverrides}>
                        <Select
                            onChange={handleSetBasics}
                            value={vaultBasicsSelection}
                            required
                            css={
                                topDown
                                    ? customerStatFilterSingleSelectTopDownCSS
                                    : customerStatFilterSingleSelectCSS
                            }
                            key={`${filterRowId}-Basics`}
                        >
                            <Option key="Yes" value="Yes">
                                Yes
                            </Option>
                            <Option key="No" value="No">
                                No
                            </Option>
                        </Select>
                    </ThemeProvider>
                );
            case 'Vault Size':
                return (
                    <ThemeProvider theme={themeOverrides}>
                        <Select
                            multiple
                            onChange={handleSetVaultSizes}
                            value={vaultSize}
                            required
                            css={getMultiSelectCSS()}
                            key={`${filterRowId}-Size`}
                        >
                            <Option key="small" value="small">
                                Small
                            </Option>
                            <Option key="medium" value="medium">
                                Medium
                            </Option>
                            <Option key="large" value="large">
                                Large
                            </Option>
                            <Option key="veryLarge" value="veryLarge">
                                Very Large
                            </Option>
                            <Option key="extraLarge" value="extraLarge">
                                Extra Large
                            </Option>
                            <Option key="full" value="full">
                                Full
                            </Option>
                        </Select>
                    </ThemeProvider>
                );
            case 'POD':
                return (
                    <ThemeProvider theme={themeOverrides}>
                        <Select
                            multiple
                            onChange={handleSetPod}
                            value={vaultPod}
                            required
                            css={getMultiSelectCSS()}
                            key={`${filterRowId}-POD`}
                        >
                            {podNames?.map((podName) =>
                                getShortenedPodName(podName).length !== 0 ? (
                                    <Option key={getShortenedPodName(podName)} value={podName}>
                                        {getShortenedPodName(podName)}
                                    </Option>
                                ) : null,
                            )}
                        </Select>
                    </ThemeProvider>
                );
            case 'Customer Size':
                return (
                    <ThemeProvider theme={themeOverrides}>
                        <Select
                            multiple
                            onChange={handleSetCustomerSizes}
                            value={customerSize}
                            required
                            css={getMultiSelectCSS()}
                            key={`${filterRowId}-Customer-Sizes`}
                        >
                            {customerSizes?.map((s) => (
                                <Option key={s} value={s}>
                                    {s}
                                </Option>
                            ))}
                        </Select>
                    </ThemeProvider>
                );
            case 'Customer Region':
                return (
                    <ThemeProvider theme={themeOverrides}>
                        <Select
                            multiple
                            onChange={handleSetRegions}
                            value={region}
                            required
                            css={getMultiSelectCSS()}
                            key={`${filterRowId}-Customer-Regions`}
                        >
                            {regions?.map((r) => (
                                <Option key={r} value={r}>
                                    {r}
                                </Option>
                            ))}
                        </Select>
                    </ThemeProvider>
                );
            case 'Customer Industry':
                return (
                    <ThemeProvider theme={themeOverrides}>
                        <Select
                            multiple
                            onChange={handleSetIndustries}
                            value={industry}
                            required
                            css={getMultiSelectCSS()}
                            key={`${filterRowId}-Customer-Industries`}
                        >
                            {industries?.map((r) => (
                                <Option key={r} value={r}>
                                    {r}
                                </Option>
                            ))}
                        </Select>
                    </ThemeProvider>
                );
            case 'Customer Segment':
                return (
                    <ThemeProvider theme={themeOverrides}>
                        <Select
                            multiple
                            onChange={handleSetSegments}
                            value={segment}
                            required
                            css={getMultiSelectCSS()}
                            key={`${filterRowId}-Customer-Segments`}
                        >
                            {segments?.map((s) => (
                                <Option key={s} value={s}>
                                    {s}
                                </Option>
                            ))}
                        </Select>
                    </ThemeProvider>
                );
            case 'Additional Info':
                return (
                    <input
                        value={filter?.instanceDescription ?? ''}
                        onChange={handleSetInstanceDescription}
                        required
                        css={customerStatFilterInstanceDescriptionInputCSS}
                        key={`${filterRowId}-Additional Info`}
                    />
                );
            default:
                return null;
        }
    }
}
FilterSelectionMenu.propTypes = {
    applications: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string,
            label: PropTypes.string,
            stats: PropTypes.arrayOf(
                PropTypes.shape({
                    applicationKey: PropTypes.string,
                    key: PropTypes.string,
                    label: PropTypes.string,
                }),
            ),
        }),
    ),
    customers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            label: PropTypes.string,
        }),
    ),
    filter: PropTypes.shape({
        appFamilyKeys: PropTypes.arrayOf(PropTypes.string),
        appKeys: PropTypes.arrayOf(PropTypes.string),
        applications: PropTypes.arrayOf(PropTypes.string),
        customerIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
        customerSizes: PropTypes.arrayOf(PropTypes.string),
        industries: PropTypes.arrayOf(PropTypes.string),
        instanceDescription: PropTypes.string,
        podNames: PropTypes.arrayOf(PropTypes.string),
        regions: PropTypes.arrayOf(PropTypes.string),
        segments: PropTypes.arrayOf(PropTypes.string),
        vaultBasics: PropTypes.string,
        vaultSizes: PropTypes.arrayOf(PropTypes.string),
    }),
    filterRowId: PropTypes.number,
    filterStat: PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
        type: PropTypes.string,
    }),
    filterStatType: PropTypes.string,
    filterType: PropTypes.string,
    handleSetFilters: PropTypes.func,
    mobile: PropTypes.bool,
    operator: PropTypes.string,
    setFilterStatType: PropTypes.func,
    topDown: PropTypes.bool,
};
